/**
 * Little Puku Embedded Bundle Builder v3.2
 * ===========================================================================
 * High-performance, conversion-focused commerce widget
 *
 * Features:
 * - Tab-based navigation with filmstrip
 * - Dynamic discount tier tracking (15% → 20% → 30%)
 * - Summary stack (F, W, C, P bubbles)
 * - Live price calculation
 * - Multi-item add to cart
 *
 * @see /_reference/bundle-widget-development-plan.md
 */

(function (window, document) {
  'use strict';

  // ===========================================================================
  // CONSTANTS
  // ===========================================================================

  const DARK_COLORS = ['#5c4033', '#57534e', '#2d2a26', '#3b3633', '#4a4541'];

  // ===========================================================================
  // PRICE CALCULATOR
  // ===========================================================================

  class PriceCalculator {
    constructor(discountTiers) {
      this.discountTiers = discountTiers || [
        { minCount: 4, percent: 30 },
        { minCount: 3, percent: 20 },
        { minCount: 2, percent: 15 },
        { minCount: 1, percent: 0 }
      ];
      this.selections = new Map(); // categoryKey -> { handle, price, variantId, color, name }
    }

    setSelection(categoryKey, data) {
      if (!data || data.value === 'none') {
        this.selections.delete(categoryKey);
      } else {
        this.selections.set(categoryKey, {
          handle: data.value,
          price: parseFloat(data.price) || 0,
          variantId: data.variantId || null,
          color: data.color || '#e3dcd2',
          name: data.name || data.value,
          image: data.image || null
        });
      }
    }

    getSubtotal() {
      let total = 0;
      this.selections.forEach(selection => {
        total += selection.price;
      });
      return total;
    }

    getItemCount() {
      return this.selections.size;
    }

    getCurrentTier() {
      const count = this.getItemCount();
      for (const tier of this.discountTiers) {
        if (count >= tier.minCount) {
          return tier;
        }
      }
      return { minCount: 0, percent: 0 };
    }

    getNextTier() {
      const count = this.getItemCount();
      const sortedTiers = [...this.discountTiers].sort((a, b) => a.minCount - b.minCount);
      return sortedTiers.find(tier => tier.minCount > count) || null;
    }

    getDiscountAmount() {
      const tier = this.getCurrentTier();
      const subtotal = this.getSubtotal();
      return subtotal * (tier.percent / 100);
    }

    getTotal() {
      return this.getSubtotal() - this.getDiscountAmount();
    }

    getSelection(categoryKey) {
      return this.selections.get(categoryKey) || null;
    }

    hasSelection(categoryKey) {
      return this.selections.has(categoryKey);
    }

    getAllSelections() {
      return Array.from(this.selections.entries()).map(([category, data]) => ({
        category,
        ...data
      }));
    }
  }

  // ===========================================================================
  // CART INTEGRATION
  // ===========================================================================

  class CartIntegration {
    constructor() {
      this.isProcessing = false;
    }

    // BUG-FIX: Add retry logic for transient network failures
    async fetchWithRetry(url, options, retryCount = 0) {
      const MAX_RETRIES = 2;
      const RETRY_DELAYS = [1000, 2000];

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      try {
        const response = await fetch(url, { ...options, signal: controller.signal });
        clearTimeout(timeoutId);

        if (response.ok) {
          return response;
        } else if (response.status >= 500 && retryCount < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[retryCount]));
          return this.fetchWithRetry(url, options, retryCount + 1);
        }
        return response;
      } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError' && retryCount < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[retryCount]));
          return this.fetchWithRetry(url, options, retryCount + 1);
        }
        throw error;
      }
    }

    async addItems(items) {
      if (this.isProcessing) return { success: false, error: 'Already processing' };
      if (items.length === 0) return { success: false, error: 'No items to add' };

      this.isProcessing = true;

      try {
        // Filter out items without valid variant IDs (mock mode)
        const validItems = items.filter(item => item.variantId && item.variantId.length > 0);

        if (validItems.length === 0) {
          // BUG-FIX: Improved mock mode messaging
          console.warn('[BundleBuilder] No valid variant IDs - running in preview mode');
          await new Promise(resolve => setTimeout(resolve, 800));
          return { success: true, cart: null, mock: true, message: 'Preview mode - no products added' };
        }

        // BUG-FIX: Warn if some items were skipped
        if (validItems.length < items.length) {
          console.warn(`[BundleBuilder] ${items.length - validItems.length} items skipped (no variant ID)`);
        }

        const payload = {
          items: validItems.map(item => ({
            id: item.variantId,
            quantity: 1
          }))
        };

        // BUG-FIX: Use retry logic for transient failures
        const response = await this.fetchWithRetry('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.description || 'Failed to add to cart');
        }

        // Fetch fresh cart with retry
        const cartResponse = await this.fetchWithRetry('/cart.js', {});
        const cart = await cartResponse.json();

        // Dispatch events for cart drawer
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        document.dispatchEvent(new CustomEvent('cart:added')); // BUG-FIX: Add cart:added event

        // Open cart drawer if available
        if (window.wetheme && typeof window.wetheme.updateCartDrawer === 'function') {
          window.wetheme.updateCartDrawer(cart);

          // BUG-FIX: Dispatch cart:open event to actually open the drawer
          // updateCartDrawer only updates content, doesn't open it
          // TIMING: Standard cart animation delay (300ms per cart-system audit)
          setTimeout(() => {
            const cartDrawer = document.querySelector('#cartSlideoutWrapper');
            if (cartDrawer) {
              cartDrawer.dispatchEvent(new CustomEvent('cart:open'));
            }
          }, 300);
        }

        return { success: true, cart };

      } catch (error) {
        if (error.name === 'AbortError') {
          console.error('[BundleBuilder] Cart request timed out after retries');
          return { success: false, error: 'Request timed out - please try again' };
        }
        console.error('[BundleBuilder] Add to cart error:', error);
        return { success: false, error: error.message };

      } finally {
        this.isProcessing = false;
      }
    }
  }

  // ===========================================================================
  // MAIN BUILDER CLASS
  // ===========================================================================

  class BundleBuilder {
    constructor(container, config) {
      this.container = container;
      this.config = config;

      // State
      this.activeTab = null;
      this.state = {};

      // Initialize state from config categories
      config.categories.forEach(cat => {
        this.state[cat.key] = null;
      });

      // Initialize sub-controllers
      this.priceCalculator = new PriceCalculator(config.discountTiers);
      this.cartIntegration = new CartIntegration();

      // Cache DOM references
      this.cacheElements();

      // Bind events
      this.bindEvents();

      // Set initial state
      this.setDefaultSelections();

      // Activate first tab
      if (config.categories.length > 0) {
        this.switchTab(config.categories[0].key);
      }

      // Initial UI update
      this.updateAllUI();

      // Initialize Omnisend observer
      this.initOmnisendObserver();
    }

    cacheElements() {
      // Main containers
      this.stageEl = this.container.querySelector('[data-builder-stage]');
      this.controlsEl = this.container.querySelector('[data-builder-controls]');

      // Image
      this.mainImageEl = this.container.querySelector('[data-main-image]');
      this.expandHintEl = this.container.querySelector('[data-expand-hint]');

      // Bundle Savings Elements (New)
      this.milestoneProgressEls = this.container.querySelectorAll('[data-milestone-progress]');
      this.milestoneMessageEls = this.container.querySelectorAll('[data-milestone-message]');
      this.milestoneFillEls = this.container.querySelectorAll('[data-milestone-fill]');
      this.milestoneDotEls = this.container.querySelectorAll('[data-milestone-dot]');
      this.milestoneLabelEls = this.container.querySelectorAll('[data-milestone-label]'); // For bottom labels

      // Filmstrip
      this.filmstripEl = this.container.querySelector('[data-filmstrip]');
      this.tabElements = this.container.querySelectorAll('[data-tab]');
      this.checkBadges = {};
      this.container.querySelectorAll('[data-check-badge]').forEach(badge => {
        const key = badge.dataset.checkBadge;
        this.checkBadges[key] = badge;
      });

      // Panels
      this.panelsEl = this.container.querySelector('[data-panels]');
      this.panelElements = {};
      this.container.querySelectorAll('[data-panel]').forEach(panel => {
        const key = panel.dataset.panel;
        this.panelElements[key] = panel;
      });

      // Footer
      this.totalPriceEl = this.container.querySelector('[data-total-price]');
      this.savingsBadgeEl = this.container.querySelector('[data-savings-badge]');
      this.summaryStackEl = this.container.querySelector('[data-summary-stack]');
      this.addButtonEl = this.container.querySelector('[data-add-to-cart]');

    }

    bindEvents() {
      // Tab clicks
      this.tabElements.forEach(tab => {
        tab.addEventListener('click', () => {
          this.switchTab(tab.dataset.tab);
        });
      });

      // Variant selection changes
      const variantInputs = this.container.querySelectorAll('input[type="radio"][data-category]');
      variantInputs.forEach(input => {
        input.addEventListener('change', (e) => this.onVariantChange(e));
      });

      // Add to cart button
      if (this.addButtonEl) {
        this.addButtonEl.addEventListener('click', () => this.onAddToCart());
      }

      // Image tap to expand (mobile only)
      if (this.mainImageEl) {
        this.mainImageEl.addEventListener('click', () => this.toggleExpand());
      }

      // Expand hint click (for "Show Options" when expanded)
      if (this.expandHintEl) {
        this.expandHintEl.addEventListener('click', () => this.toggleExpand());
      }

      // Scroll arrows for overflow categories
      this.initScrollArrows();
    }

    initScrollArrows() {
      const optionsContainers = this.container.querySelectorAll('[data-options-container]');
      const builderWrapper = this.container.querySelector('[data-builder-wrapper]');

      optionsContainers.forEach(container => {
        const wrapper = container.closest('.builder-options-wrapper');
        if (!wrapper) return;

        const scrollArrow = wrapper.querySelector('[data-view-more]');
        if (!scrollArrow) return;

        // Click to scroll right
        scrollArrow.addEventListener('click', () => {
          const scrollAmount = container.offsetWidth * 0.8;
          container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        });

        // Fade out arrow when scrolled to end
        container.addEventListener('scroll', () => {
          const isNearEnd = container.scrollLeft + container.offsetWidth >= container.scrollWidth - 20;
          scrollArrow.classList.toggle('hidden', isNearEnd);
        });
      });

      // Details accordion toggle
      this.container.querySelectorAll('[data-details-toggle]').forEach(toggle => {
        toggle.addEventListener('click', () => {
          this.toggleAccordion(toggle);
        });
      });

      // Scroll to details CTA
      this.container.querySelectorAll('[data-scroll-to-details]').forEach(btn => {
        btn.addEventListener('click', () => {
          const panel = btn.closest('[data-panel]');
          if (!panel) return;

          const accordionContainer = panel.querySelector('.builder-details-accordions');
          if (!accordionContainer) return;

          // Scroll the main controls container
          const controls = this.container.querySelector('[data-builder-controls]');
          const scrollable = panel.querySelector('.builder-scrollable');
          if (scrollable && accordionContainer) {
            // Precise scroll within container to avoid viewport jumping
            const targetY = accordionContainer.offsetTop - 10;
            scrollable.scrollTo({ top: targetY, behavior: 'smooth' });
          }

          // Automatically open the first accordion if none are open
          const firstAccordion = accordionContainer.querySelector('[data-details-toggle]');
          if (firstAccordion && firstAccordion.getAttribute('aria-expanded') === 'false') {
            this.toggleAccordion(firstAccordion);
          }
        });
      });
    }

    toggleAccordion(toggle) {
      const accordion = toggle.closest('[data-details-accordion]');
      const builderWrapper = this.container.querySelector('[data-builder-wrapper]');
      if (accordion) {
        const isOpen = accordion.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        const content = accordion.querySelector('[data-details-content]');
        if (content) {
          if (isOpen) {
            content.style.maxHeight = content.scrollHeight + 'px';
            requestAnimationFrame(() => {
              content.style.maxHeight = content.scrollHeight + 'px';
            });
          } else {
            content.style.maxHeight = '0px';
          }
        }
      }
      if (builderWrapper) {
        const hasOpen = !!this.container.querySelector('[data-details-accordion].is-open');
        builderWrapper.classList.toggle('builder-accordion-open', hasOpen);
      }
    }

    initOmnisendObserver() {
      if (!('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            document.body.classList.add('builder-active');
          } else {
            document.body.classList.remove('builder-active');
          }
        });
      }, {
        threshold: 0.2 // Trigger when 20% of the builder is visible
      });

      observer.observe(this.container);
      this.omnisendObserver = observer;
    }

    // =========================================================================
    // TAB SWITCHING
    // =========================================================================

    switchTab(tabName) {
      this.activeTab = tabName;

      // Update tab states
      this.tabElements.forEach(tab => {
        if (tab.dataset.tab === tabName) {
          tab.classList.add('tab-active');
          tab.classList.remove('tab-inactive');
        } else {
          tab.classList.remove('tab-active');
          tab.classList.add('tab-inactive');
        }
      });

      // Show/hide panels
      Object.entries(this.panelElements).forEach(([key, panel]) => {
        if (key === tabName) {
          panel.classList.add('panel-active');
        } else {
          panel.classList.remove('panel-active');
        }
      });

      // Update main image to show this category's product
      this.updateImageForCategory(tabName);
    }

    updateImageForCategory(categoryKey) {
      // Find the category config
      const category = this.config.categories.find(c => c.key === categoryKey);
      if (!category) return;

      // Check if there's already a selection for this category
      const selection = this.priceCalculator.getSelection(categoryKey);

      if (selection && selection.productImage) {
        // Show selected variant's product image
        this.updatePreviewImage(selection.productImage, selection.name);
      } else if (selection && selection.image) {
        // Fallback to swatch image
        this.updatePreviewImage(selection.image, selection.name);
      } else if (category.featuredImage) {
        // No selection yet - show category's featured image
        this.updatePreviewImage(category.featuredImage, category.title);
      }
    }

    // =========================================================================
    // VARIANT CHANGE HANDLING
    // =========================================================================

    onVariantChange(event) {
      const input = event.target;
      const categoryKey = input.dataset.category;
      const value = input.value;
      const price = input.dataset.price || '0';
      const variantId = input.dataset.variantId || '';
      const color = input.dataset.color || '#e3dcd2';
      const name = input.dataset.name || value;
      const image = input.dataset.image || '';
      const productImage = input.dataset.productImage || '';

      // Update state
      this.state[categoryKey] = value === 'none' ? null : value;

      // Update price calculator
      this.priceCalculator.setSelection(categoryKey, {
        value,
        price,
        variantId,
        color,
        name,
        image,
        productImage
      });

      // Update preview image with animation (use product image, fallback to swatch)
      this.updatePreviewImage(productImage || image, name);

      // Update all UI
      this.updateAllUI();

      // Emit event for external listeners
      this.emitSelectionChanged();
    }

    updatePreviewImage(imageUrl, variantName) {
      if (!this.mainImageEl) return;
      if (!imageUrl) return;

      // Add transition effect
      this.mainImageEl.style.opacity = '0.7';
      this.mainImageEl.style.transform = 'scale(0.98)';

      setTimeout(() => {
        this.mainImageEl.src = imageUrl;
        this.mainImageEl.alt = variantName || this.container.dataset.i18nBundlePreview || 'Bundle preview';
        this.mainImageEl.style.opacity = '1';
        this.mainImageEl.style.transform = 'scale(1)';
      }, 200);
    }

    // =========================================================================
    // SET DEFAULT SELECTIONS
    // =========================================================================

    setDefaultSelections() {
      this.config.categories.forEach(category => {
        const inputs = this.container.querySelectorAll(
          `input[type="radio"][data-category="${category.key}"]`
        );

        // Find first checked option
        let defaultInput = null;
        inputs.forEach(input => {
          if (input.checked && input.value !== 'none') {
            defaultInput = input;
          }
        });

        if (defaultInput) {
          const categoryKey = defaultInput.dataset.category;
          const value = defaultInput.value;
          const price = defaultInput.dataset.price || '0';
          const variantId = defaultInput.dataset.variantId || '';
          const color = defaultInput.dataset.color || '#e3dcd2';
          const name = defaultInput.dataset.name || value;
          const image = defaultInput.dataset.image || '';
          const productImage = defaultInput.dataset.productImage || '';

          this.state[categoryKey] = value;
          this.priceCalculator.setSelection(categoryKey, {
            value,
            price,
            variantId,
            color,
            name,
            image,
            productImage
          });
        }
      });
    }

    // =========================================================================
    // UI UPDATE METHODS
    // =========================================================================

    updateAllUI() {
      this.updateDiscountMeter();
      this.updateFilmstripChecks();
      this.updateSummaryStack();
      this.updatePriceDisplay();
      this.updateButtonState();
    }

    updateDiscountMeter() {
      const count = this.priceCalculator.getItemCount();

      // Implement Brief Logic:
      // 1 item: 0% width, Dots inactive
      // 2 items: 0% width, Dot 1 active
      // 3 items: 50% width, Dot 1,2 active
      // 4+ items: 100% width, Dot 1,2,3 active

      let fillPercent = 0;
      if (count < 3) {
        fillPercent = 0;
      } else if (count === 3) {
        fillPercent = 50;
      } else {
        fillPercent = 100;
      }

      // Update bar width
      this.milestoneFillEls.forEach(el => {
        el.style.width = `${fillPercent}%`;
      });

      // Update dots state
      // Dots data-milestone-dot are "2", "3", "4" corresponding to item counts
      this.milestoneDotEls.forEach(dot => {
        const milestone = parseInt(dot.dataset.milestoneDot, 10);
        // Active/Green if count >= milestone
        const isActive = count >= milestone;
        dot.classList.toggle('inactive', !isActive);
      });

      // Update Labels (bottom row) state
      this.milestoneLabelEls.forEach(label => {
        const milestone = parseInt(label.dataset.milestoneLabel, 10);
        const isActive = count >= milestone;
        label.classList.toggle('inactive', !isActive);
      });

      // Update Guidance Text (CTA). Translations come from data-i18n-* attributes
      // on the section root (set in hub-builder-360.liquid); English fallback if absent.
      const ds = this.container.dataset;
      let message = '';
      let isMax = false;

      if (count <= 1) {
        message = ds.i18nAddMore15 || 'Add 1 more for 15% Off!';
      } else if (count === 2) {
        message = ds.i18nAddMore20 || 'Add 1 more for 20% Off!';
      } else if (count === 3) {
        message = ds.i18nAddMore30 || 'Add 1 more for 30% Off!';
      } else {
        message = ds.i18nMaxUnlocked || 'MAX unlocked!';
        isMax = true;
      }

      this.milestoneMessageEls.forEach(el => {
        el.textContent = message;
        el.classList.toggle('max-reached', isMax);
      });
    }

    updateFilmstripChecks() {
      this.config.categories.forEach(category => {
        const badge = this.checkBadges[category.key];
        if (!badge) return;

        const selection = this.priceCalculator.getSelection(category.key);

        if (selection) {
          badge.classList.add('visible');
        } else {
          badge.classList.remove('visible');
        }
      });
    }

    updateSummaryStack() {
      if (!this.summaryStackEl) return;

      // Clear existing bubbles
      this.summaryStackEl.innerHTML = '';

      const categoryOrder = this.config.categories.map(c => c.key);
      let bubbleIndex = 0;

      categoryOrder.forEach(categoryKey => {
        const selection = this.priceCalculator.getSelection(categoryKey);
        if (!selection) return;

        const category = this.config.categories.find(c => c.key === categoryKey);
        if (!category) return;

        let bgColor = selection.color;

        // For wraps, if "match" is selected, use the footrest color
        if (categoryKey === 'wraps' && selection.handle === 'match') {
          const footrestSelection = this.priceCalculator.getSelection('footrest');
          if (footrestSelection) {
            bgColor = footrestSelection.color;
          }
        }

        // Determine if color is dark
        const isDark = this.isColorDark(bgColor);
        const colorClass = isDark ? 'bubble-dark' : 'bubble-light';

        // Create bubble element
        const bubble = document.createElement('div');
        bubble.className = `builder-summary-bubble ${colorClass} builder-animate-stack-enter`;
        bubble.style.backgroundColor = bgColor;
        bubble.style.zIndex = 10 - bubbleIndex;
        bubble.style.animationDelay = `${bubbleIndex * 0.05}s`;
        bubble.textContent = category.label;

        this.summaryStackEl.appendChild(bubble);
        bubbleIndex++;
      });
    }

    updatePriceDisplay() {
      const total = this.priceCalculator.getTotal();
      const savings = this.priceCalculator.getDiscountAmount();
      const tier = this.priceCalculator.getCurrentTier();

      if (this.totalPriceEl) {
        this.totalPriceEl.textContent = this.formatMoney(total);
      }

      if (this.savingsBadgeEl) {
        if (savings > 0) {
          this.savingsBadgeEl.style.display = 'inline-block';
          // Through formatMoney, not a hardcoded '$': the total beside it is in
          // the presentment currency, so a literal dollar sign put "£76.50" next
          // to "SAVED $14" in the same badge row.
          this.savingsBadgeEl.textContent = `SAVED ${this.formatMoney(savings)}`;
        } else {
          this.savingsBadgeEl.style.display = 'none';
        }
      }
    }

    updateButtonState() {
      const count = this.priceCalculator.getItemCount();

      if (this.addButtonEl) {
        this.addButtonEl.disabled = count === 0;
      }
    }

    // =========================================================================
    // ADD TO CART
    // =========================================================================

    async onAddToCart() {
      const items = this.priceCalculator.getAllSelections();

      if (items.length === 0) {
        return;
      }

      this.showButtonState('loading');

      const result = await this.cartIntegration.addItems(items);

      if (result.success) {
        this.showButtonState('success');
        setTimeout(() => {
          this.showButtonState('default');
        }, 2500);
      } else {
        this.showButtonState('error', result.error);
        setTimeout(() => {
          this.showButtonState('default');
        }, 3000);
      }
    }

    showButtonState(state, message = null) {
      if (!this.addButtonEl) return;

      const defaultText = this.config.buttonText || 'Add Kit to Cart';
      const loadingText = this.config.buttonLoadingText || 'Adding...';

      switch (state) {
        case 'loading':
          this.addButtonEl.disabled = true;
          this.addButtonEl.classList.add('btn-loading');
          this.addButtonEl.innerHTML = `
            <div class="builder-spinner"></div>
            ${loadingText}
          `;
          break;

        case 'success':
          this.addButtonEl.disabled = true;
          this.addButtonEl.classList.remove('btn-loading');
          this.addButtonEl.classList.add('btn-success');
          this.addButtonEl.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            Added to Cart!
          `;
          break;

        case 'error':
          this.addButtonEl.disabled = false;
          this.addButtonEl.classList.remove('btn-loading', 'btn-success');
          this.addButtonEl.textContent = message || 'Error - Try Again';
          break;

        default:
          this.addButtonEl.disabled = this.priceCalculator.getItemCount() === 0;
          this.addButtonEl.classList.remove('btn-loading', 'btn-success');
          this.addButtonEl.innerHTML = `
            ${defaultText}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          `;
      }
    }

    // =========================================================================
    // MOBILE EXPAND/COLLAPSE
    // =========================================================================

    toggleExpand() {
      // Only functional on mobile
      if (window.innerWidth >= 1024) return;

      const wrapper = this.container.querySelector('.builder-wrapper');
      if (!wrapper) return;

      const isExpanded = wrapper.classList.contains('stage-expanded');

      if (isExpanded) {
        // Collapse: restore normal layout
        wrapper.classList.remove('stage-expanded');
        if (this.expandHintEl) {
          this.expandHintEl.innerHTML = `
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 4px;">
              <polyline points="15 3 21 3 21 9"></polyline>
              <polyline points="9 21 3 21 3 15"></polyline>
              <line x1="21" y1="3" x2="14" y2="10"></line>
              <line x1="3" y1="21" x2="10" y2="14"></line>
            </svg>
            Tap to View
          `;
        }
      } else {
        // Expand: make stage fill most of the screen
        wrapper.classList.add('stage-expanded');
        if (this.expandHintEl) {
          this.expandHintEl.innerHTML = `
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 4px;">
              <polyline points="4 14 10 14 10 20"></polyline>
              <polyline points="20 10 14 10 14 4"></polyline>
              <line x1="14" y1="10" x2="21" y2="3"></line>
              <line x1="3" y1="21" x2="10" y2="14"></line>
            </svg>
            Show Options
          `;
        }
      }
    }

    // =========================================================================
    // UTILITY METHODS
    // =========================================================================

    isColorDark(hexColor) {
      if (!hexColor) return false;

      // Check against known dark colors
      if (DARK_COLORS.includes(hexColor.toLowerCase())) {
        return true;
      }

      // Calculate luminance
      let hex = hexColor.replace('#', '');
      if (hex.length === 3) {
        hex = hex.split('').map(c => c + c).join('');
      }

      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);

      // Using relative luminance formula
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

      return luminance < 0.5;
    }

    formatMoney(amount) {
      // Presentment currency first. window.theme is NEVER defined in this theme —
      // the theme object is exposed as window.wetheme (theme.js:39534) — so the
      // old `window.theme && window.theme.formatMoney` check could not match and
      // every non-AU amount fell through to the hardcoded '$'. Locale pinned to
      // 'en' to match the theme's leading-symbol convention.
      const pc = this.config && this.config.presentmentCurrency;
      const sc = this.config && this.config.shopCurrency;
      if (pc && sc && pc !== sc && typeof Intl !== 'undefined') {
        try {
          return new Intl.NumberFormat('en', {
            style: 'currency', currency: pc, currencyDisplay: 'narrowSymbol',
          }).format(amount);
        } catch (e) {
          try {
            return new Intl.NumberFormat('en', { style: 'currency', currency: pc })
              .format(amount);
          } catch (e2) { /* unknown code — fall through */ }
        }
      }
      // Deliberately NOT window.wetheme.formatMoney: it returns money HTML
      // (<span class="money">…</span>) and every caller here assigns the result to
      // textContent, so the markup would be shown literally. Reviving that branch
      // was a regression; the shop format below is plain text, as this contract
      // requires.
      const format = (this.config && this.config.moneyFormat) || '${{amount}}';
      const value = (amount).toFixed(2);
      return format
        .replace('{{amount}}', value)
        .replace('{{amount_no_decimals}}', Math.round(amount))
        .replace('{{amount_with_comma_separator}}', value.replace('.', ','));
    }

    emitSelectionChanged() {
      const detail = {
        count: this.priceCalculator.getItemCount(),
        items: this.priceCalculator.getAllSelections(),
        subtotal: this.priceCalculator.getSubtotal(),
        tier: this.priceCalculator.getCurrentTier()
      };

      document.dispatchEvent(new CustomEvent('builder:selection-changed', { detail }));
    }
  }

  // ===========================================================================
  // INITIALIZATION
  // ===========================================================================

  /**
   * Apply swatch colors from data attributes
   * Shopify strips inline style attributes, so we use data attributes instead
   */
  function applySwatchColors() {
    document.querySelectorAll('.builder-swatch[data-swatch-color]').forEach(swatch => {
      const color = swatch.dataset.swatchColor;
      if (color) {
        swatch.style.backgroundColor = color;
      }
    });
    document.querySelectorAll('.builder-swatch[data-swatch-image]').forEach(swatch => {
      const image = swatch.dataset.swatchImage;
      if (image) {
        swatch.style.backgroundImage = `url('${image}')`;
      }
    });
  }

  function initBundleBuilder() {
    // Apply swatch colors first (Shopify strips inline styles)
    applySwatchColors();

    const containers = document.querySelectorAll('[data-bundle-builder]');

    containers.forEach(container => {
      // Skip if already initialized
      if (container.dataset.builderInitialized) return;

      const configElement = container.querySelector('[data-builder-config]');
      if (!configElement) {
        console.warn('[BundleBuilder] Config element not found');
        return;
      }

      try {
        const config = JSON.parse(configElement.textContent);
        const instance = new BundleBuilder(container, config);

        // Store reference
        container._bundleBuilder = instance;
        container.dataset.builderInitialized = 'true';

        // Set default milestone position to 5 (scale-fit layout)
        // Position 1 kept as option for future experimentation
        if (!document.body.classList.contains('milestone-position-2') &&
          !document.body.classList.contains('milestone-position-3') &&
          !document.body.classList.contains('milestone-position-4') &&
          !document.body.classList.contains('milestone-position-5')) {
          document.body.classList.add('milestone-position-5');
        }

      } catch (error) {
        console.error('[BundleBuilder] Failed to initialize:', error);
      }
    });
  }

  // Initialize on DOM ready - moved to after ScrollTrap class definition
  // See initBundleBuilderWithTrap below

  // Theme editor support
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      const section = e.target;
      // Remove initialized flag to allow re-init
      const builder = section.querySelector('[data-bundle-builder]');
      if (builder) {
        delete builder.dataset.builderInitialized;
        if (section._scrollTrap) {
          section._scrollTrap.destroy();
          delete section._scrollTrap;
        }
        // Use delayed init that includes scroll trap
        setTimeout(() => {
          initBundleBuilder();
          initScrollTrap();
        }, 100);
      }
    });

    document.addEventListener('shopify:section:reorder', () => {
      setTimeout(() => {
        initBundleBuilder();
        initScrollTrap();
      }, 100);
    });
  }

  // ===========================================================================
  // SCROLL SNAP - Automatic snap-to-view when section becomes visible
  // ===========================================================================
  // Uses CSS scroll-snap with JS fallback for smooth scrollIntoView
  // No scroll blocking - natural scrolling behavior preserved
  // ===========================================================================

  class ScrollTrap {
    constructor(section, options = {}) {
      this.section = section;
      this.options = {
        threshold: options.threshold || 0.7, // How much of section must be visible to snap
        ...options
      };

      this.isEngaged = false;
      this.hasSnapped = false; // Only snap once per scroll-into-view

      this.init();
    }

    init() {
      // Set up Intersection Observer for snap trigger
      this.observer = new IntersectionObserver(
        (entries) => this.handleIntersection(entries),
        { threshold: [0, 0.3, 0.5, 0.7, 0.9, 1] }
      );
      this.observer.observe(this.section);
    }

    handleIntersection(entries) {
      const entry = entries[0];
      const visibleRatio = entry.intersectionRatio;

      if (visibleRatio >= this.options.threshold && !this.isEngaged) {
        this.engage();
      } else if (visibleRatio < 0.1 && this.isEngaged) {
        this.release();
      }
    }

    engage() {
      if (this.isEngaged) return;

      this.isEngaged = true;
      this.section.classList.add('scroll-trapped');
      document.body.classList.add('scroll-trap-active');

      // Hide back-to-top button while engaged (target the custom element)
      const backToTop = document.querySelector('back-to-top');
      if (backToTop) {
        backToTop.style.opacity = '0';
        backToTop.style.pointerEvents = 'none';
        backToTop.style.visibility = 'hidden';
      }

      // Smooth scroll section into view (only once per engagement)
      if (!this.hasSnapped) {
        this.section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.hasSnapped = true;
      }
    }

    release() {
      if (!this.isEngaged) return;

      this.isEngaged = false;
      this.hasSnapped = false; // Reset for next scroll-into-view
      this.section.classList.remove('scroll-trapped');
      document.body.classList.remove('scroll-trap-active');

      // Restore back-to-top button
      const backToTop = document.querySelector('back-to-top');
      if (backToTop) {
        backToTop.style.opacity = '';
        backToTop.style.pointerEvents = '';
        backToTop.style.visibility = '';
      }
    }

    destroy() {
      this.release();
      this.observer.disconnect();
    }
  }

  // Initialize scroll trap for bundle builder sections
  function initScrollTrap() {
    const sections = document.querySelectorAll('.builder-section');

    sections.forEach(section => {
      // Skip if already initialized or if user prefers reduced motion
      if (section._scrollTrap) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      section._scrollTrap = new ScrollTrap(section, {
        threshold: 0.05  // Snap as soon as widget becomes visible (5% visible)
      });
    });
  }

  // Initialize scroll trap after bundle builder
  function initBundleBuilderWithTrap() {
    initBundleBuilder();

    // Small delay to ensure section is ready
    requestAnimationFrame(() => {
      initScrollTrap();
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundleBuilderWithTrap);
  } else {
    initBundleBuilderWithTrap();
  }

  // Expose for debugging
  window.BundleBuilder = BundleBuilder;
  window.ScrollTrap = ScrollTrap;
  window.initBundleBuilder = initBundleBuilderWithTrap;

  // ===========================================================================
  // MILESTONE POSITION TOGGLE (Console Testing)
  // Usage: window.setMilestonePosition(1|2|3|4|5)
  //   1 = In header (original)
  //   2 = Above filmstrip (card style)
  //   3 = Above main image
  //   4 = Above filmstrip (flat/inline style)
  //   5 = Flat + scale-fit (no scroll) [DEFAULT]
  // ===========================================================================
  window.setMilestonePosition = function (position) {
    const validPositions = [1, 2, 3, 4, 5];
    if (!validPositions.includes(position)) {
      console.error('Invalid position. Use 1, 2, 3, 4, or 5.');
      console.log('  1 = In header (original)');
      console.log('  2 = Above filmstrip (card)');
      console.log('  3 = Above main image');
      console.log('  4 = Above filmstrip (flat)');
      console.log('  5 = Flat + scale-fit (no scroll) [DEFAULT]');
      return;
    }

    // Remove all position classes first
    document.body.classList.remove('milestone-position-1', 'milestone-position-2', 'milestone-position-3', 'milestone-position-4', 'milestone-position-5');

    // Add new position class
    // Position 5 is default (added during init), position 1 needs explicit class to override
    if (position === 1) {
      document.body.classList.add('milestone-position-1');
    } else if (position === 2) {
      document.body.classList.add('milestone-position-2');
    } else if (position === 3) {
      document.body.classList.add('milestone-position-3');
    } else if (position === 4) {
      document.body.classList.add('milestone-position-4');
    } else if (position === 5) {
      document.body.classList.add('milestone-position-5');
    }

    console.log(`Milestone position set to ${position}:`);
    const descriptions = {
      1: 'In header (original)',
      2: 'Above filmstrip (card)',
      3: 'Above main image',
      4: 'Above filmstrip (flat)',
      5: 'Flat + scale-fit (no scroll) [DEFAULT]'
    };
    console.log(`  → ${descriptions[position]}`);
  };

  // Convenience aliases
  window.mp = window.setMilestonePosition;

})(window, document);
